'use client';

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Users, Search, Plus, Trash2, Edit2, X, Mail, Phone, Building2, MapPin, Download, Upload, Filter, UserCheck,
  Globe, Briefcase, ChevronRight, Activity, ShieldCheck, ExternalLink,
  Crosshair, Zap, Clock, Fingerprint, MoreVertical, Smartphone,
  ChevronLeft
} from 'lucide-react';
import { fetchContacts, createContact, updateContact, deleteContact } from '../../redux/crmSlice';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import api from '../../services/api';
import ImportModal from '../../components/crm/ImportModal';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkActionBar from '../../components/crm/BulkActionBar';
import SavedViewsBar from '../../components/crm/SavedViewsBar';
import FilterBuilder from '../../components/crm/FilterBuilder';
import InlineEditSelect from '../../components/crm/InlineEditSelect';
import RowActionsMenu from '../../components/crm/RowActionsMenu';
import { downloadCsv } from '../../utils/exportCsv';
import { CountrySelect, StateSelect } from '../../components/CountryStateSelect';
import { SUPPORTED_CURRENCIES } from '../../constants/currencies';

import PageHeader from '../../components/PageHeader';
import { PAGE, PageLoader } from '../../components/crm/ui';
const LIFECYCLE_STAGE_OPTIONS = [
  { value: 'subscriber', label: 'SUBSCRIBER' },
  { value: 'lead', label: 'LEAD' },
  { value: 'mql', label: 'MQL' },
  { value: 'sql', label: 'SQL' },
  { value: 'opportunity', label: 'OPPORTUNITY' },
  { value: 'customer', label: 'CUSTOMER' },
  { value: 'evangelist', label: 'EVANGELIST' },
  { value: 'other', label: 'OTHER' },
];

const CONTACT_FILTER_FIELDS = [
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'phoneNumber', label: 'Phone' },
  { value: 'company', label: 'Company' },
  { value: 'designation', label: 'Designation' },
  { value: 'lifecycleStage', label: 'Lifecycle Stage' },
  { value: 'city', label: 'City' },
  { value: 'country', label: 'Country' },
];

export default function ContactsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { contacts, contactsMeta, loading, error } = useSelector(state => state.crm);
  const bulk = useBulkSelection();
  const PAGE_SIZE = 20;

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phoneNumber: '', company: '', designation: '', city: '', state: '', country: '', defaultCurrency: '', status: 'active'
  });
  const [showMenu, setShowMenu] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [customersOnly, setCustomersOnly] = useState(false);
  const [advancedConditions, setAdvancedConditions] = useState([]);

  // Reset to page 1 whenever the search term changes so results aren't scoped to a stale page
  useEffect(() => {
    setPage(1);
  }, [searchQuery, customersOnly, advancedConditions]);

  const advancedFiltersParam = (() => {
    const valid = advancedConditions.filter(c => c.field && c.value !== '');
    return valid.length ? JSON.stringify(valid) : undefined;
  })();

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchContacts({ page, limit: PAGE_SIZE, search: searchQuery || undefined, lifecycleStage: customersOnly ? 'customer' : undefined, advancedFilters: advancedFiltersParam }));
    }, searchQuery ? 350 : 0);
    return () => clearTimeout(timer);
  }, [dispatch, page, searchQuery, customersOnly, advancedFiltersParam]);

  const filtered = contacts;
  const totalPages = contactsMeta?.pages || 1;
  const totalContacts = contactsMeta?.total ?? contacts.length;

  const handleAdd = () => {
    setEditingId(null);
    setFormData({ name: '', email: '', phoneNumber: '', company: '', designation: '', city: '', state: '', country: '', defaultCurrency: '', status: 'active' });
    setShowAddModal(true);
  };

  const handleEdit = (contact) => {
    setEditingId(contact._id);
    setFormData(contact);
    setShowAddModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveLoading(true);
    try {
      if (editingId) {
        await dispatch(updateContact({ id: editingId, data: formData })).unwrap();
        alertService.success('Updated', 'Contact record updated');
      } else {
        await dispatch(createContact(formData)).unwrap();
        alertService.success('Success', 'New contact added');
      }
      setShowAddModal(false);
    } catch (err) {
      alertService.error('Error', err || 'Action failed');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (contact) => {
    const confirmed = await alertService.confirm('Delete Contact?', `Permanently delete "${contact.name}"?`, 'danger', 'Delete');
    if (confirmed) {
      try {
        await dispatch(deleteContact(contact._id)).unwrap();
        alertService.success('Deleted', 'Contact removed');
      } catch (err) { alertService.error('Error', err || 'Action failed'); }
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/crm/contacts/export', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alertService.error('Export Failed', err.response?.data?.message || 'Could not export contacts');
    }
  };

  const runBulkAction = async (action, value) => {
    try {
      const res = await api.post('/crm/contacts/bulk', { contactIds: bulk.selectedIds, action, value });
      const count = res.data?.modifiedCount ?? bulk.selectedCount;
      bulk.clear();
      dispatch(fetchContacts({ page, limit: PAGE_SIZE, search: searchQuery || undefined }));
      alertService.success(action === 'delete' ? 'Deleted' : 'Updated', `${count} contact(s) ${action === 'delete' ? 'deleted' : 'updated'}`);
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Bulk action failed');
    }
  };

  const handleBulkStageChange = (value) => runBulkAction('change_lifecycle_stage', value);
  const handleBulkDelete = () => runBulkAction('delete');

  const handleBulkExport = () => {
    const selected = contacts.filter(c => bulk.selected.has(c._id));
    downloadCsv(
      'contacts-selected.csv',
      ['Name', 'PhoneNumber', 'Email', 'Company', 'JobTitle'],
      selected.map(c => [c.name, c.phoneNumber, c.email, c.company, c.designation])
    );
  };

  if (loading && contacts.length === 0) {
    return (
      <PageLoader />
    );
  }

  return (
    <div className={PAGE}>
      
      {/* Header Area */}
      <PageHeader
        title="Contacts"
        subtitle={<>{totalContacts} Contacts &middot; Page {contactsMeta?.page || 1} of {totalPages}</>}
        icon={Users}
        actions={<>
<div className="relative flex flex-wrap items-center gap-2">
<button onClick={handleExport} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] transition-all flex items-center gap-2 whitespace-nowrap shrink-0">
            <Upload size={14} /> Export
          </button>
          <button onClick={handleAdd} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] transition-all flex items-center gap-2 whitespace-nowrap shrink-0">
            <Plus size={14} /> New Contact
          </button>
          <button
            onClick={() => setShowMenu(v => !v)}
            title="More actions"
            aria-label="More actions"
            className="h-9 w-9 flex items-center justify-center bg-white border border-[#D8DDE6] rounded text-[#706E6B] hover:bg-[#F3F2F2] transition-all"
          >
            <MoreVertical size={16} />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-11 z-20 w-48 bg-white border border-[#D8DDE6] rounded shadow-[0_2px_8px_rgba(0,0,0,0.16)] overflow-hidden">
              <button
                onClick={() => { setShowMenu(false); setShowImportModal(true); }}
                className="w-full text-left px-4 py-2.5 text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] flex items-center gap-2"
              >
                <Download size={14} /> Import
              </button>
            </div>
          )}
</div>
  </>}
      />

      <ImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Contacts"
        importEndpoint="/crm/contacts/import"
        sampleHeaders={['Name', 'PhoneNumber', 'Email', 'JobTitle', 'Notes']}
        sampleRow={['John Smith', '9998887777', 'john@example.com', 'Procurement Manager', 'Met at trade show']}
        onImported={() => dispatch(fetchContacts({ page: 1, limit: PAGE_SIZE }))}
      />

      {/* Control Bar */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg p-3 flex flex-wrap items-center gap-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[240px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
          <input 
            type="text" 
            placeholder="Search by name, email, company…" 
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 bg-[#F3F2F2] px-3 h-9 rounded border border-[#D8DDE6] cursor-pointer">
            <input
              type="checkbox"
              checked={customersOnly}
              onChange={e => setCustomersOnly(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#0176D3] cursor-pointer"
            />
            <Filter size={12} className="text-[#706E6B]" />
            <span className="text-[12px] font-bold text-[#080707] uppercase">Customers Only</span>
          </label>

          <FilterBuilder
            fields={CONTACT_FILTER_FIELDS}
            conditions={advancedConditions}
            onChange={setAdvancedConditions}
          />

          <SavedViewsBar
            resourceType="contact"
            currentFilters={{ search: searchQuery, customersOnly, advancedConditions }}
            onApply={(filters) => {
              setSearchQuery(filters.search || '');
              setCustomersOnly(!!filters.customersOnly);
              setAdvancedConditions(filters.advancedConditions || []);
            }}
          />
        </div>
      </div>

      <BulkActionBar
        selectedCount={bulk.selectedCount}
        onClear={bulk.clear}
        statusOptions={LIFECYCLE_STAGE_OPTIONS}
        statusLabel="Lifecycle Stage"
        onStatusChange={handleBulkStageChange}
        onDelete={handleBulkDelete}
        onExport={handleBulkExport}
        entityLabel="contact"
      />

      {/* Contacts Table */}
      <div className="sf-table-container">
        <table className="sf-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={bulk.isAllSelected(filtered.map(c => c._id))}
                  onChange={() => bulk.toggleAll(filtered.map(c => c._id))}
                  aria-label="Select all contacts"
                  className="w-4 h-4 accent-[#0176D3] cursor-pointer"
                />
              </th>
              <th>Contact Name</th>
              <th>Organization & Role</th>
              <th>Communication</th>
              <th>Location</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? filtered.map(contact => (
              <tr key={contact._id} className="group">
                <td>
                  <input
                    type="checkbox"
                    checked={bulk.selected.has(contact._id)}
                    onChange={() => bulk.toggle(contact._id)}
                    aria-label={`Select ${contact.name}`}
                    className="w-4 h-4 accent-[#0176D3] cursor-pointer"
                  />
                </td>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#0176D3] flex items-center justify-center text-white text-[10px] font-bold shadow-sm">
                      {contact.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                    <div>
                      <p className="font-bold text-[#0176D3] hover:underline cursor-pointer" onClick={() => navigate(`/dashboard/crm/contacts/${contact._id}`)}>{contact.name}</p>
                      <InlineEditSelect
                        value={contact.lifecycleStage || 'lead'}
                        options={LIFECYCLE_STAGE_OPTIONS}
                        badge={<span className="text-[11px] text-[#706E6B] font-medium">{(contact.lifecycleStage || 'lead').toUpperCase()}</span>}
                        onSave={async (newStage) => {
                          await dispatch(updateContact({ id: contact._id, data: { lifecycleStage: newStage } }));
                          dispatch(fetchContacts({ page, limit: PAGE_SIZE, search: searchQuery || undefined, lifecycleStage: customersOnly ? 'customer' : undefined, advancedFilters: advancedFiltersParam }));
                        }}
                      />
                    </div>
                  </div>
                </td>
                <td>
                  <div className="space-y-0.5">
                    <p className="text-[13px] font-bold text-[#080707]">{contact.company || 'No company'}</p>
                    <p className="text-[11px] text-[#706E6B]">{contact.designation || 'Specialist'}</p>
                  </div>
                </td>
                <td>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-[11px] font-medium text-[#706E6B]">
                      <Mail size={10} className="text-[#0176D3]" /> {contact.email || '—'}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] font-medium text-[#706E6B]">
                      <Smartphone size={10} className="text-[#2E844A]" /> {contact.phoneNumber || '—'}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-1.5 text-[12px] font-medium text-[#706E6B]">
                    <MapPin size={12} /> {contact.city || 'Anywhere'}{contact.country ? `, ${contact.country}` : ''}
                  </div>
                </td>
                <td className="text-right">
                  <RowActionsMenu actions={[
                    { label: 'Edit Contact', icon: Edit2, onClick: () => handleEdit(contact) },
                    { label: 'View Detail', icon: ExternalLink, onClick: () => navigate(`/dashboard/crm/contacts/${contact._id}`) },
                    { label: 'Delete', icon: Trash2, danger: true, onClick: () => handleDelete(contact) },
                  ]} />
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={6} className="py-24 text-center">
                  <Users className="mx-auto mb-4 opacity-10" size={48} />
                  <p className="text-sm font-bold text-[#706E6B] uppercase tracking-[0.2em]">Zero registry matches</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalContacts > 0 && (
        <div className="flex items-center justify-between bg-white border border-[#E5E5E5] rounded-lg p-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <p className="text-[12px] font-medium text-[#706E6B]">
            Showing {((page - 1) * PAGE_SIZE) + 1}&ndash;{Math.min(page * PAGE_SIZE, totalContacts)} of {totalContacts}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="h-8 px-3 flex items-center gap-1 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <span className="text-[12px] font-bold text-[#080707] px-2">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="h-8 px-3 flex items-center gap-1 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAddModal(false)} />
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-xl z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">{editingId ? 'Edit Contact' : 'New Contact'}</h3>
              <button onClick={() => setShowAddModal(false)} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
            </div>
            
            <form onSubmit={handleSave} className="p-4 sm:p-6 space-y-5">
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Legal Name</label>
                <div className="relative">
                  <Fingerprint className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
                  <input required value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
                    <input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
                    <input required value={formData.phoneNumber} onChange={e => setFormData(p => ({ ...p, phoneNumber: e.target.value }))} className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Organization</label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
                    <input value={formData.company} onChange={e => setFormData(p => ({ ...p, company: e.target.value }))} className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Role</label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
                    <input value={formData.designation} onChange={e => setFormData(p => ({ ...p, designation: e.target.value }))} className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Country</label>
                  <CountrySelect value={formData.country} onChange={country => setFormData(p => ({ ...p, country, state: '' }))} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">State</label>
                  <StateSelect country={formData.country} value={formData.state} onChange={state => setFormData(p => ({ ...p, state }))} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">City</label>
                  <input value={formData.city} onChange={e => setFormData(p => ({ ...p, city: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Default Currency</label>
                  <select value={formData.defaultCurrency || ''} onChange={e => setFormData(p => ({ ...p, defaultCurrency: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]">
                    <option value="">Use company default</option>
                    {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="bg-white h-9 px-4 border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
                <button type="submit" disabled={saveLoading} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50">
                  {saveLoading ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
